// TodoItem.js
// Displays a single todo with expand/collapse functionality

import React, { useState } from 'react';
import {
  doc,
  updateDoc,
  deleteDoc,
  addDoc,
  collection,
  serverTimestamp,
  getDoc
} from 'firebase/firestore';
import { db } from '../firebase';
import AssigneeSelector from './AssigneeSelector';
import UpsellModal from './UpsellModal';

function TodoItem({ todo, currentUserId, familyData, userEmail, isPremium, onInspect }) {
  // Track if this todo is expanded (showing details) - used as fallback if no onInspect
  const [isExpanded, setIsExpanded] = useState(false);

  // Track form values when editing
  const [notes, setNotes] = useState(todo.notes || '');
  const [dueDate, setDueDate] = useState(todo.dueDate || '');
  const [assignee, setAssignee] = useState(todo.assignee || '');

  // Track if we're saving
  const [saving, setSaving] = useState(false);

  // Check if assignee was previously not AI but is now being changed to AI
  const [wasAI] = useState(todo.assignee === 'ai');

  // Upsell modal state
  const [showUpsellModal, setShowUpsellModal] = useState(false);

  // Toggle done/not done
  const handleToggleDone = async (event) => {
    // Stop the click from expanding the todo
    event.stopPropagation();

    try {
      const newStatus = todo.status === 'done' ? 'pending' : 'done';
      await updateDoc(doc(db, 'todos', todo.id), {
        status: newStatus,
        updatedAt: serverTimestamp()
      });

      // Create notification if task is being marked as done
      if (newStatus === 'done') {
        const actorName = familyData?.memberNames?.[currentUserId] || 'Someone';
        await addDoc(collection(db, 'notifications'), {
          familyId: todo.familyId,
          type: 'task_completed',
          actorId: currentUserId,
          actorName: actorName,
          taskId: todo.id,
          taskTitle: todo.title,
          readBy: [],
          createdAt: serverTimestamp()
        });
      }
    } catch (error) {
      console.error('Error updating todo status:', error);
    }
  };

  // Save changes to the todo
  const handleSave = async () => {
    // Check if user is trying to assign to AI without premium (and it wasn't already AI)
    if (assignee === 'ai' && !wasAI && !isPremium) {
      setShowUpsellModal(true);
      return;
    }

    setSaving(true);

    try {
      // Get assignee name for display
      let assigneeName = '';
      if (assignee === 'ai') {
        assigneeName = 'Kana (AI Assistant)';
      } else if (assignee && familyData?.memberNames) {
        assigneeName = familyData.memberNames[assignee] || 'Unknown';
      }

      // Update the todo
      await updateDoc(doc(db, 'todos', todo.id), {
        notes: notes,
        dueDate: dueDate || null,
        assignee: assignee || null,
        assigneeName: assigneeName,
        updatedAt: serverTimestamp()
      });

      // If assignee changed to AI and wasn't AI before, create an ai_tasks document
      if (assignee === 'ai' && !wasAI) {
        await addDoc(collection(db, 'ai_tasks'), {
          todoId: todo.id,
          familyId: todo.familyId,
          title: todo.title,
          details: notes,
          requestedBy: currentUserId,
          requestedByEmail: userEmail,
          status: 'pending',
          createdAt: serverTimestamp()
        });
      }

      // Collapse the expanded section
      setIsExpanded(false);
    } catch (error) {
      console.error('Error saving todo:', error);
      alert('Failed to save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Delete the todo
  const handleDelete = async () => {
    // Confirm before deleting
    if (!window.confirm('Are you sure you want to delete this todo?')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'todos', todo.id));
    } catch (error) {
      console.error('Error deleting todo:', error);
      alert('Failed to delete todo. Please try again.');
    }
  };

  // Format date for display
  const formatDueDate = (dateString) => {
    if (!dateString) return null;

    const date = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date < today) {
      return { text: 'Overdue', isOverdue: true };
    } else if (date.toDateString() === today.toDateString()) {
      return { text: 'Today', isOverdue: false };
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return { text: 'Tomorrow', isOverdue: false };
    } else {
      return {
        text: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        isOverdue: false
      };
    }
  };

  const dueDateInfo = formatDueDate(todo.dueDate);

  return (
    <>
      <div className="todo-item">
        {/* Main row - always visible */}
        <div
          className="todo-item-header"
          onClick={() => onInspect ? onInspect() : setIsExpanded(!isExpanded)}
        >
          {/* Checkbox */}
          <div
            className={`todo-checkbox ${todo.status === 'done' ? 'checked' : ''}`}
            onClick={handleToggleDone}
          />

          {/* Content */}
          <div className="todo-content">
            <div className={`todo-title ${todo.status === 'done' ? 'completed' : ''}`}>
              {todo.title}
            </div>

            <div className="todo-meta">
              {/* Show assignee - always visible */}
              <span className={`todo-assignee ${todo.assignee === 'ai' ? 'ai' : ''} ${!todo.assigneeName ? 'unassigned' : ''}`}>
                {todo.assigneeName ? (
                  <>
                    {todo.assignee === 'ai' ? '🤖 ' : ''}
                    {todo.assigneeName}
                  </>
                ) : (
                  'Unassigned'
                )}
              </span>

              {/* Show due date - always visible */}
              <span className={`todo-due-date ${dueDateInfo?.isOverdue ? 'overdue' : ''} ${!dueDateInfo ? 'no-date' : ''}`}>
                {dueDateInfo ? dueDateInfo.text : 'No due date'}
              </span>
            </div>

            {/* AI status badge */}
            {todo.assignee === 'ai' && todo.status !== 'done' && (
              <div className="ai-status-badge">
                Kana is working on this
              </div>
            )}
          </div>

          {/* Expand icon - only show if using inline expand (no onInspect) */}
          {!onInspect && (
            <span className={`todo-expand-icon ${isExpanded ? 'expanded' : ''}`}>
              ▼
            </span>
          )}
        </div>

        {/* Expanded section - only visible when expanded and no onInspect */}
        {!onInspect && isExpanded && (
          <div className="todo-expanded">
            {/* AI Callout - show if assigned to AI */}
            {assignee === 'ai' && (
              <div className="ai-callout">
                <div className="ai-callout-title">
                  🤖 Assigned to Kana (AI Assistant)
                </div>
                <p>
                  More details = better results! Add context below about what you need.
                  A new email thread will be created with your AI agent to handle this task.
                </p>
              </div>
            )}

            {/* Notes field */}
            <div className="form-group">
              <label htmlFor={`notes-${todo.id}`}>Notes</label>
              <textarea
                id={`notes-${todo.id}`}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add details, context, or instructions..."
              />
            </div>

            {/* Due date field */}
            <div className="form-group">
              <label htmlFor={`dueDate-${todo.id}`}>Due date</label>
              <input
                type="date"
                id={`dueDate-${todo.id}`}
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
              />
            </div>

            {/* Assignee field */}
            <div className="form-group">
              <label htmlFor={`assignee-${todo.id}`}>Assigned to</label>
              <AssigneeSelector
                id={`assignee-${todo.id}`}
                value={assignee}
                onChange={setAssignee}
                familyData={familyData}
                currentUserId={currentUserId}
                isPremium={isPremium}
              />
            </div>

            {/* Action buttons */}
            <div className="todo-actions">
              <button
                onClick={handleSave}
                className="button button-primary button-small"
                disabled={saving}
                style={{ flex: 2 }}
              >
                {saving ? 'Saving...' : 'Save changes'}
              </button>
              <button
                onClick={handleDelete}
                className="button button-danger button-small"
                style={{ flex: 1 }}
              >
                Delete
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Upsell Modal */}
      {showUpsellModal && (
        <UpsellModal onClose={() => setShowUpsellModal(false)} />
      )}
    </>
  );
}

export default TodoItem;
